import { captureException as defaultCaptureException } from '@sentry/browser'

/** Same as Error, but improves Sentry monitoring reports */
class UnexpectedNullError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'UnexpectedNullError'
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, UnexpectedNullError)
    }
  }
}

export interface AssumeNonNullDeps {
  captureException: (error: Error) => void
}

/**
 * Returns value. Detects null or undefined values and logs themto pass through.
 *
 * This will functionally work as the non-null assertion suffix operator `!`
 * (aka "believe me it's not null", yet sometimes ...is null). Unlike the
 * operator, this function will log to Sentry and the browser's console the
 * ORIGIN source line of a null value, before any injection or deferral, to
 * track better where it actually comes from. It's purpose improve observability
 * and gradual removal of the `!` habit.
 *
 * Moving forward functions that actually `throw` an error (like invariant,
 * assert, ensureNonNull) are encouraged.
 *
 * @example
 * ```ts
 * const data = getDataMaybe()!; // BAD
 * const data = assumeNonNull(getDataMaybe()); // BETTER
 * ```
 *
 * @param value Value to check
 * @param message Optional message to improve telemetry
 * @param deps Injected dependencies; defaults to real Sentry (tests pass a stub)
 * @returns Unmodified `value` parameter, but TS-typed as non-null non-undefined
 */
export function assumeNonNull<T>(
  value: T | undefined | null,
  message?: string,
  deps: AssumeNonNullDeps = { captureException: defaultCaptureException },
): NonNullable<T> {
  if (value === undefined || value === null) {
    const valuePart = value === null ? 'null' : 'undefined'
    const messagePart = message ? `, ${message}` : ''
    const error = new UnexpectedNullError(`assumeNonNull: Unexpected ${valuePart}${messagePart}`)

    // NOTE: This `if` gets stripped at build time (it works with browsers)
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(error.message)
    }

    deps.captureException(error)
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return value!
}
